import { useState, useEffect, useRef, useCallback, useId } from 'react';

/**
 * AutocompleteInput
 *
 * A debounced autocomplete input that queries /autocomplete with cascading
 * context. Selecting a genus narrows species; selecting a species can
 * auto-fill common name.
 *
 * Props:
 *   field         — 'genus' | 'species' | 'common_name'
 *   value         — controlled value
 *   onChange      — (value: string) => void
 *   onSelect      — (suggestion: object) => void  fired when user picks a suggestion
 *   genusValue    — current genus (required for species + common_name fields)
 *   speciesValue  — current species (required for common_name field)
 *   placeholder   — input placeholder text
 *   className     — extra class names for the input element
 *   disabled      — disable the input
 */
function AutocompleteInput({
  field,
  value,
  onChange,
  onSelect,
  genusValue = '',
  speciesValue = '',
  placeholder = '',
  className = '',
  disabled = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const listboxId = useId();

  // Track whether the user has interacted with this field yet.
  // Prevents auto-fetching on edit page load when values are pre-filled.
  const hasInteracted = useRef(false);

  // Suppress the debounce effect that fires immediately after a selection.
  // Without this, selecting a suggestion calls onChange → value changes →
  // the value useEffect fires → fetchSuggestions → dropdown reopens.
  const justSelected = useRef(false);

  const debounceTimer = useRef(null);
  const containerRef = useRef(null);

  // -- Fetch suggestions -----------------------------------------------------

  // Abort controller for the current in-flight request. Cancelling on
  // handleSelect prevents a late response from reopening the dropdown.
  const abortRef = useRef(null);

  const fetchSuggestions = useCallback((query, allowEmpty = false) => {
    // Genus requires 2+ chars — no context to narrow by without typing.
    if (field === 'genus' && (!query || query.length < 2)) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    // Other fields respect allowEmpty when context is available.
    if (!allowEmpty && (!query || query.length < 2)) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    const params = new URLSearchParams({ limit: 10 });

    if (field === 'genus') {
      params.set('genus', query);
    } else if (field === 'species') {
      if (!genusValue) return;
      params.set('genus', genusValue);
      params.set('species', query || '');
    } else if (field === 'common_name') {
      if (!genusValue || !speciesValue) return;
      params.set('genus', genusValue);
      // Use only the base species epithet (first word) to avoid cultivar mismatches
      params.set('species', speciesValue.split(' ')[0]);
      params.set('common_name', query || '');
    }

    // Cancel any previous in-flight request before starting a new one
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const { signal } = abortRef.current;

    setLoading(true);
    fetch(`/autocomplete?${params}`, { signal })
      .then(r => r.json())
      .then(data => {
        setSuggestions(data.results || []);
        setOpen((data.results || []).length > 0);
        setActiveIndex(-1);
      })
      .catch(err => {
        if (err.name !== 'AbortError') setSuggestions([]);
      })
      .finally(() => {
        // Only clear loading if this is still the active request.
        // Aborted requests should not clear loading while a newer one is in flight.
        if (abortRef.current?.signal === signal || !abortRef.current) {
          setLoading(false);
        }
      });
  }, [field, genusValue, speciesValue]);

  // Debounce after user interaction to avoid triggering on edit page load when
  // values are pre-filled by the form.
  useEffect(() => {
    if (!hasInteracted.current) return;
    // Skip the cycle that fires because handleSelect programmatically changed value.
    if (justSelected.current) {
      justSelected.current = false;
      return;
    }
    clearTimeout(debounceTimer.current);
    const allowEmpty = (field === 'species' && genusValue) ||
                       (field === 'common_name' && genusValue && speciesValue);
    debounceTimer.current = setTimeout(() => fetchSuggestions(value, Boolean(allowEmpty)), 300);
    return () => clearTimeout(debounceTimer.current);
  }, [fetchSuggestions, field, genusValue, speciesValue, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // -- Handle suggestion selection ------------------------------------------

  const handleSelect = (suggestion) => {
    justSelected.current = true; // suppress the next debounce effect cycle
    // Abort any in-flight request so a late response can't reopen the dropdown
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    onChange(suggestion.value);
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
    if (onSelect) onSelect(suggestion);
  };

  // -- Keyboard navigation --------------------------------------------------

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && open && suggestions.length > 0) {
      e.preventDefault();
      // Select highlighted item, or first item if none highlighted
      handleSelect(suggestions[activeIndex >= 0 ? activeIndex : 0]);
      return;
    }

    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  // -- Render ---------------------------------------------------------------

  const labelForSuggestion = (s) => {
    if (s.type === 'genus') return s.value;
    if (s.type === 'species') {
      return s.value;
    }
    if (s.type === 'common_name') return s.value;
    return s.value;
  };

  const metaForSuggestion = (s) => {
    const parts = [];
    if (s.family) parts.push(s.family);
    if (s.source === 'gbif') parts.push('GBIF');
    return parts.join(' · ');
  };

  return (
    <div className="autocomplete-wrapper" ref={containerRef}>
      <input
        type="text"
        className={`form-input ${className}`}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onChange={e => {
          hasInteracted.current = true;
          onChange(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          hasInteracted.current = true;
          // Always fetch fresh on focus rather than re-showing stale cached results
          if (field === 'genus' && value && value.length >= 2) {
            fetchSuggestions(value, false);
          } else if (field === 'species' && genusValue) {
            fetchSuggestions(value, true);
          } else if (field === 'common_name' && genusValue && speciesValue) {
            fetchSuggestions(value, true);
          }
        }}
        onBlur={() => {
          // Clear suggestions on blur so they don't reappear on next focus
          // without a fresh fetch. Use a small delay so mousedown on an option
          // fires before the suggestions disappear.
          setTimeout(() => {
            setSuggestions([]);
            setOpen(false);
          }, 150);
        }}
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        role="combobox"
      />

      {loading && <span className="autocomplete-loading">…</span>}

      {open && suggestions.length > 0 && (
        <ul id={listboxId} className="autocomplete-dropdown" role="listbox">
          {suggestions.map((s, i) => (
            <li
              key={i}
              role="option"
              aria-selected={i === activeIndex}
              className={`autocomplete-option${i === activeIndex ? ' autocomplete-option-active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(s);
              }}
            >
              <span className="autocomplete-option-label">{labelForSuggestion(s)}</span>
              {metaForSuggestion(s) && (
                <span className="autocomplete-option-meta">{metaForSuggestion(s)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AutocompleteInput;
